import type { ReactNode } from 'react';

import { cn } from '../../../lib/utils.js';
import { Avatar } from '../../ui/avatar.js';
import { Text } from '../../ui/typography.js';

export interface ListTablePrimaryCellProps {
  title: ReactNode;
  subtitle?: ReactNode;
  avatarSrc?: string;
  /** Initials or short label inside avatar when `leading` is omitted. */
  avatarLabel?: string;
  /** Replaces default avatar (e.g. vehicle plate badge). */
  leading?: ReactNode;
  badges?: ReactNode;
  className?: string;
}

export function listTablePrimaryInitials(label: string): string {
  return label
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
}

export function ListTablePrimaryCell({
  title,
  subtitle,
  avatarSrc,
  avatarLabel,
  leading,
  badges,
  className,
}: ListTablePrimaryCellProps) {
  const resolvedAvatarLabel =
    avatarLabel ?? (typeof title === 'string' ? listTablePrimaryInitials(title) : undefined);

  return (
    <div className={cn('list-table-primary-cell', className)}>
      {leading ?? (
        <Avatar
          size="sm"
          {...(avatarSrc ? { src: avatarSrc } : {})}
          aria-hidden={avatarSrc ? undefined : true}
        >
          {resolvedAvatarLabel}
        </Avatar>
      )}
      <div className="list-table-primary-cell__stack">
        <div className="list-table-primary-cell__title-row">
          <span className="list-table-primary-cell__title">{title}</span>
          {badges ? <span className="list-table-primary-cell__badges">{badges}</span> : null}
        </div>
        {subtitle ? (
          <Text variant="small" muted className="list-table-primary-cell__subtitle">
            {subtitle}
          </Text>
        ) : null}
      </div>
    </div>
  );
}
